import { useState, useCallback, useRef, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
  ZoomableGroup,
} from 'react-simple-maps';
import { entityColor } from '../../lib/atlasColors';
import { cn } from '../../lib/cn';

const BASEMAP_URL = '/data/atlas/basemap-50m.json';

function AtlasMap({
  geoData,
  selectedYear,
  overlayData,
  center,
  scale,
  projection,
  onEntityHover,
  onEntityClick,
}) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 960, height: 500 });
  const [position, setPosition] = useState({ coordinates: center || [10, 20], zoom: 1 });

  // Measure the container so the SVG fills it properly
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height) setDimensions({ width, height });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Reset position when center changes (e.g. region switch) — compare by value
  const centerKey = center ? `${center[0]},${center[1]}` : '10,20';
  useEffect(() => {
    setPosition({ coordinates: center || [10, 20], zoom: 1 });
  }, [centerKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMoveEnd = useCallback((pos) => {
    setPosition(pos);
  }, []);

  const handleZoomIn = useCallback(() => {
    setPosition((pos) => ({ ...pos, zoom: Math.min(pos.zoom * 1.5, 20) }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setPosition((pos) => ({ ...pos, zoom: Math.max(pos.zoom / 1.5, 0.5) }));
  }, []);

  const handleReset = useCallback(() => {
    setPosition({ coordinates: center || [10, 20], zoom: 1 });
  }, [center]);

  const handleEntityEnter = useCallback(
    (properties, e) => {
      onEntityHover?.(properties, { x: e.clientX, y: e.clientY });
    },
    [onEntityHover]
  );

  const handleEntityLeave = useCallback(() => {
    onEntityHover?.(null, { x: 0, y: 0 });
  }, [onEntityHover]);

  const projectionName = projection || 'geoNaturalEarth1';
  const mapCenter = center || [10, 20];
  const mapScale = scale || 160;

  // Base country style for the political basemap
  const hasHistoricalData = geoData && geoData.features && geoData.features.length > 0;
  const basemapStyle = hasHistoricalData
    ? {
        // Muted background when historical layer is on top
        default: { fill: '#f5f0e8', stroke: '#c9c0b0', strokeWidth: 0.4, outline: 'none' },
        hover:   { fill: '#f5f0e8', stroke: '#c9c0b0', strokeWidth: 0.4, outline: 'none' },
        pressed: { fill: '#f5f0e8', stroke: '#c9c0b0', strokeWidth: 0.4, outline: 'none' },
      }
    : {
        // Interactive political map when it's the only layer
        default: { fill: '#e8e0d0', stroke: '#b8ad9a', strokeWidth: 0.5, outline: 'none' },
        hover:   { fill: '#d5cbb8', stroke: '#8a7e6e', strokeWidth: 0.8, outline: 'none', cursor: 'pointer' },
        pressed: { fill: '#ccc1ab', stroke: '#8a7e6e', strokeWidth: 0.8, outline: 'none' },
      };

  // Prevent browser's native pinch-zoom on the map container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const preventBrowserZoom = (e) => {
      if (e.ctrlKey) e.preventDefault();
    };
    el.addEventListener('wheel', preventBrowserZoom, { passive: false });
    return () => el.removeEventListener('wheel', preventBrowserZoom);
  }, []);

  return (
    <div ref={containerRef} className="w-full h-full relative" style={{ background: '#dce7f0', touchAction: 'none' }}>
      <ComposableMap
        projection={projectionName}
        projectionConfig={{
          center: mapCenter,
          scale: mapScale,
        }}
        width={dimensions.width}
        height={dimensions.height}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={handleMoveEnd}
          minZoom={0.5}
          maxZoom={20}
          filterZoomEvent={(e) => !e.button}
        >
          {/* Layer 1: Basemap — modern political boundaries */}
          <Geographies geography={BASEMAP_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={basemapStyle}
                  onMouseEnter={(e) => {
                    if (geo.properties?.name) {
                      handleEntityEnter({ Name: geo.properties.name }, e);
                    }
                  }}
                  onMouseLeave={handleEntityLeave}
                />
              ))
            }
          </Geographies>

          {/* Layer 2: Historical political boundaries — Cliopatria snapshots */}
          {geoData && (
            <Geographies geography={geoData}>
              {({ geographies }) =>
                geographies
                  .filter((geo) => geo.properties?.NAME)
                  .map((geo, index) => {
                    const name = geo.properties.NAME || geo.properties.Name;
                    return (
                      <Geography
                        key={`${name}-${index}`}
                        geography={geo}
                        style={{
                          default: {
                            fill: entityColor(name),
                            stroke: '#333',
                            strokeWidth: 0.5,
                            fillOpacity: 0.65,
                            outline: 'none',
                          },
                          hover: {
                            fill: entityColor(name),
                            stroke: '#000',
                            strokeWidth: 1.5,
                            fillOpacity: 0.85,
                            outline: 'none',
                            cursor: 'pointer',
                          },
                          pressed: {
                            fill: entityColor(name),
                            stroke: '#000',
                            strokeWidth: 1.5,
                            fillOpacity: 0.9,
                            outline: 'none',
                          },
                        }}
                        onMouseEnter={(e) => handleEntityEnter({ Name: name, ...geo.properties }, e)}
                        onMouseLeave={handleEntityLeave}
                        onClick={() => onEntityClick?.(geo.properties)}
                      />
                    );
                  })
              }
            </Geographies>
          )}

          {/* Layer 3: Trade route overlays */}
          {overlayData?.tradeRoutes?.features?.map((route, i) => {
            const { year_start, year_end } = route.properties;
            if (year_start && year_start > selectedYear) return null;
            if (year_end && year_end < selectedYear) return null;

            return (
              <Line
                key={`route-${i}`}
                coordinates={route.geometry.coordinates}
                stroke="#c0392b"
                strokeWidth={2}
                strokeDasharray="6 3"
                strokeOpacity={0.8}
                strokeLinecap="round"
              />
            );
          })}

          {/* Layer 4: City markers */}
          {overlayData?.cities?.features
            ?.filter((city) => {
              const year = city.properties.year;
              return !year || year <= selectedYear;
            })
            .map((city, i) => (
              <Marker key={`city-${i}`} coordinates={city.geometry.coordinates}>
                <circle r={3} fill="#2c3e50" stroke="#fff" strokeWidth={1} />
                <text
                  textAnchor="middle"
                  y={-8}
                  style={{
                    fontSize: 8,
                    fill: '#333',
                    fontFamily: 'system-ui, sans-serif',
                    fontWeight: 500,
                    paintOrder: 'stroke',
                    stroke: '#f5f0e8',
                    strokeWidth: 2,
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  }}
                >
                  {city.properties.name}
                </text>
              </Marker>
            ))}

          {/* Layer 5: Battle markers */}
          {overlayData?.battles?.features
            ?.filter((battle) => {
              const year = battle.properties.year;
              return !year || Math.abs(year - selectedYear) <= 25;
            })
            .map((battle, i) => (
              <Marker key={`battle-${i}`} coordinates={battle.geometry.coordinates}>
                <g transform="translate(-4, -4)">
                  <line x1="0" y1="0" x2="8" y2="8" stroke="#e74c3c" strokeWidth={2} />
                  <line x1="8" y1="0" x2="0" y2="8" stroke="#e74c3c" strokeWidth={2} />
                </g>
                <text
                  textAnchor="middle"
                  y={-10}
                  style={{
                    fontSize: 7,
                    fill: '#e74c3c',
                    fontFamily: 'system-ui, sans-serif',
                    fontWeight: 600,
                    paintOrder: 'stroke',
                    stroke: '#f5f0e8',
                    strokeWidth: 2,
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  }}
                >
                  {battle.properties.name} ({battle.properties.year})
                </text>
              </Marker>
            ))}
        </ZoomableGroup>
      </ComposableMap>

      {/* Zoom controls */}
      <div className={cn(
        'absolute top-4 right-4 z-10 flex flex-col gap-1',
      )}>
        <button
          onClick={handleZoomIn}
          className={cn(
            'w-9 h-9 flex items-center justify-center',
            'bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm',
            'border border-gray-300 dark:border-gray-600',
            'rounded-t-lg text-gray-700 dark:text-gray-200',
            'hover:bg-white dark:hover:bg-gray-700 transition-colors',
            'text-lg font-medium leading-none shadow-sm'
          )}
          title={t('atlas.zoomIn')}
        >
          +
        </button>
        <button
          onClick={handleZoomOut}
          className={cn(
            'w-9 h-9 flex items-center justify-center',
            'bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm',
            'border border-gray-300 dark:border-gray-600',
            'text-gray-700 dark:text-gray-200',
            'hover:bg-white dark:hover:bg-gray-700 transition-colors',
            'text-lg font-medium leading-none shadow-sm'
          )}
          title={t('atlas.zoomOut')}
        >
          −
        </button>
        <button
          onClick={handleReset}
          className={cn(
            'w-9 h-9 flex items-center justify-center',
            'bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm',
            'border border-gray-300 dark:border-gray-600',
            'rounded-b-lg text-gray-500 dark:text-gray-400',
            'hover:bg-white dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors',
            'text-xs font-medium shadow-sm'
          )}
          title={t('atlas.resetView')}
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>

      {/* Zoom level indicator */}
      {position.zoom !== 1 && (
        <div className="absolute top-4 right-16 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-gray-500 dark:text-gray-400 shadow-sm border border-gray-200 dark:border-gray-700">
          {Math.round(position.zoom * 100)}%
        </div>
      )}
    </div>
  );
}

export default memo(AtlasMap);
